import React from "react";
import { useParams, Link } from "react-router-dom";
import { Check } from "lucide-react";
import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLang } from "@/i18n";
import { Reveal } from "@/components/site/motion";
import ContactForm from "@/components/site/ContactForm";
import { SERVICES } from "@/data/content";
import Seo from "@/components/site/Seo";

export default function ServiceDetail() {
    const { slug } = useParams();
    const { t, lang } = useLang();
    const service = SERVICES.find((s) => s.slug === slug);

    if (!service) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4">
                <p className="text-slate-500">Servizio non trovato.</p>
                <Link to="/servizi" className="text-cb-accent">← {t.servicesPage.title}</Link>
            </div>
        );
    }
    const s = service[lang];
    const Icon = service.icon;

    return (
        <article data-testid="service-detail">
            <Seo title={s.name} description={s.desc} image={service.image} path={`/servizi/${slug}`} />
            <section className="relative min-h-[70vh] flex items-end pt-40 pb-16 overflow-hidden bg-cb-darker">
                <div className="absolute inset-0">
                    <img src={service.image} alt={s.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-cb-darker/75" />
                    <div className="absolute inset-0 bg-gradient-to-t from-cb-darker to-transparent" />
                </div>
                <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border border-white/15 inline-flex p-4 mb-6">
                        <Icon size={40} strokeWidth={1.2} className="text-cb-accent" />
                    </motion.div>
                    <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tighter text-white max-w-3xl">{s.name}</h1>
                    <p className="mt-5 max-w-2xl text-slate-300 text-lg">{s.short}</p>
                </div>
            </section>

            <section className="py-20 md:py-28 bg-white">
                <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-14">
                    <div className="lg:col-span-2">
                        <Reveal as="h2" className="font-display text-3xl font-semibold tracking-tight text-cb-dark">{s.name}</Reveal>
                        <Reveal delay={0.4} className="mt-5 text-slate-600 text-lg leading-relaxed">{s.desc}</Reveal>

                        <Reveal delay={0.6} as="h3" className="mt-14 font-display text-2xl font-semibold text-cb-dark">{t.servicesPage.benefits}</Reveal>
                        <div className="mt-6 grid sm:grid-cols-2 gap-4">
                            {s.benefits.map((b, i) => (
                                <Reveal key={b} delay={0.7 + i * 0.2} className="flex items-start gap-3 border border-slate-200 p-5">
                                    <Check size={20} className="text-cb-accent shrink-0 mt-0.5" />
                                    <span className="text-slate-700">{b}</span>
                                </Reveal>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-1">
                        <div className="sticky top-28 bg-cb-dark text-white p-8">
                            <h3 className="font-display text-xl font-semibold">{t.servicesPage.askQuote}</h3>
                            <div className="mt-6">
                                <ContactForm kind="quote" defaultService={s.name} dark />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-28 bg-cb-light">
                <div className="max-w-7xl mx-auto px-6">
                    <Reveal as="h2" className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-cb-dark mb-14">{t.servicesPage.process}</Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200">
                        {t.process.map((p, i) => (
                            <Reveal key={p.t} delay={i * 0.3} className="bg-white p-8">
                                <span className="font-display text-4xl font-bold text-cb-accent/30">{String(i + 1).padStart(2, "0")}</span>
                                <h3 className="mt-4 font-display text-xl font-semibold text-cb-dark">{p.t}</h3>
                                <p className="mt-2 text-slate-500 leading-relaxed">{p.d}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-28 bg-white">
                <div className="max-w-3xl mx-auto px-6">
                    <Reveal as="h2" className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-cb-dark mb-10">{t.servicesPage.faq}</Reveal>
                    <Accordion type="single" collapsible className="w-full" data-testid="service-faq">
                        {t.faq.map((f, i) => (
                            <AccordionItem key={f.q} value={`item-${i}`} className="border-b border-slate-200">
                                <AccordionTrigger className="text-left font-display text-lg text-cb-dark hover:text-cb-accent hover:no-underline py-5">{f.q}</AccordionTrigger>
                                <AccordionContent className="text-slate-600 leading-relaxed text-base">{f.a}</AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>
        </article>
    );
}
